import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import PlayCircle from "@redotech/redo-icons/play-circle.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { TagList } from "@redotech/redo-merchant-fulfillment-app/common/tag-list";
import {
  AddOrderTagsActionStep,
  AddTagActionStep,
  AssignFulfillmentServiceActionStep,
  AutoHoldType,
  GeneratePersonalizedSlipActionStep,
  OmsActionType,
  OmsSettableFields,
  PlaceHoldActionStep,
  PlaceHoldUntilActionStep,
  RoundWeightActionStep,
  SetAssignedUserActionStep,
  SetBuyReturnLabelActionStep,
  SetDeliveryMethodActionStep,
  SetDutiesPayerActionStep,
  SetInternalNoteActionStep,
  SetOriginLocationActionStep,
  SetOverrideUseOriginalDestinationAddressActionStep,
  SetPackingSlipTemplateActionStep,
  SetParcelActionStep,
  SetReturnAddressActionStep,
  SetShippingOptionsActionStep,
  SetShippingServiceActionStep,
  SetShippingServiceCheapestActionStep,
  SetShippingServiceCheapestWithinDeliveryDaysActionStep,
  SetShippingServiceFastestActionStep,
  SetTaxIdentifiersActionStep,
  SetThirdPartyBillingAccountActionStep,
  SetWeightActionStep,
  WeightRoundingType,
} from "@redotech/redo-model/advanced-flow/oms/actions";
import { OmsActionStep } from "@redotech/redo-model/advanced-flow/steps";
import { RedoShopifyDeliveryMethodTypeToString } from "@redotech/redo-model/fulfillments/fulfillment-delivery-method-type";
import { RedoFulfillmentLocation } from "@redotech/redo-model/fulfillments/fulfillment-location";
import { Hazmat } from "@redotech/redo-model/fulfillments/hazmat";
import {
  splitWeight,
  Weight,
  WeightFormat,
  WeightUnit,
} from "@redotech/redo-model/outbound-labels/util";
import { PillTheme } from "@redotech/redo-model/pill-theme";
import {
  FulfillmentGroupTagSource,
  Tag,
  TagKind,
} from "@redotech/redo-model/tag";
import { getTimeUnitName, TimeUnit } from "@redotech/redo-model/time";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import {
  getPrettyCarrierName,
  getPrettyServiceName,
} from "@redotech/redo-web/carrier-service-labels";
import { Flex } from "@redotech/redo-web/flex";
import { InfoTooltip } from "@redotech/redo-web/info-tooltip";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { Handle, Position } from "@xyflow/react";
import { useMemo } from "react";
import { PartialWithRequiredType } from "../../steps";
import { BaseNode } from "../base-node";
import * as NodeCSS from "../node.module.css";

export interface OmsActionNodeData {
  step: PartialWithRequiredType<OmsActionStep>;
  deleteElements: (id: string) => void;
}

function SetShippingServiceDetails({
  step,
}: {
  step: PartialWithRequiredType<SetShippingServiceActionStep>;
}) {
  const selectedServiceText =
    step.newShippingService && step.newShippingCarrier
      ? `${getPrettyCarrierName(step.newShippingCarrier)} ${getPrettyServiceName(step.newShippingService)}`
      : undefined;
  return (
    <>
      {"Set "}
      <Text fontWeight="semibold">Shipping service</Text>
      {" to "}
      <Text fontWeight="semibold">{`${selectedServiceText}`}</Text>
    </>
  );
}

function SetShippingServiceGroupDetails({
  step,
}: {
  step:
    | PartialWithRequiredType<SetShippingServiceCheapestActionStep>
    | PartialWithRequiredType<SetShippingServiceFastestActionStep>
    | PartialWithRequiredType<SetShippingServiceCheapestWithinDeliveryDaysActionStep>;
}) {
  const numServices = step.servicesToChooseFrom?.length;
  let comparator: string;
  let deliveryDaysText: string | undefined;

  if (
    step.actionType ===
    OmsActionType.SET_SHIPPING_SERVICE_CHEAPEST_WITHIN_DELIVERY_DAYS
  ) {
    comparator = "cheapest within delivery days";
    deliveryDaysText =
      "targetDeliveryDays" in step && step.targetDeliveryDays
        ? ` (${step.targetDeliveryDays} day${step.targetDeliveryDays === 1 ? "" : "s"})`
        : "";
  } else if (step.actionType === OmsActionType.SET_SHIPPING_SERVICE_CHEAPEST) {
    comparator = "cheapest";
  } else {
    comparator = "fastest";
  }

  return (
    <>
      {"Set "}
      <Text fontWeight="semibold">Shipping service</Text>
      {` to the ${comparator} of `}
      <Text fontWeight="semibold">
        {numServices
          ? `${numServices} service${numServices === 1 ? "" : "s"}`
          : undefined}
      </Text>
      {deliveryDaysText && (
        <Text fontWeight="semibold">{deliveryDaysText}</Text>
      )}
    </>
  );
}

function SetParcelDetails({
  step,
}: {
  step: PartialWithRequiredType<SetParcelActionStep>;
}) {
  const parcels = step.parcels?.length
    ? step.parcels
    : step.parcel
      ? [step.parcel]
      : [];

  const parcelNames = parcels.map((p) => p.name).join(", ");

  return (
    <>
      {"Set "}
      <Text fontWeight="semibold">Parcel{parcels.length > 1 ? "s" : ""}</Text>
      {" to "}
      <Text fontWeight="semibold">{parcelNames || "None"}</Text>
    </>
  );
}

function SetPackingSlipTemplateDetails({
  step,
}: {
  step: PartialWithRequiredType<SetPackingSlipTemplateActionStep>;
}) {
  return (
    <>
      {"Set "}
      <Text fontWeight="semibold">Packing Slip</Text>
      {" to "}
      <Text fontWeight="semibold">{step.packingSlipName || ""}</Text>
    </>
  );
}

function PersonalizedSlipDetails({
  step,
}: {
  step: PartialWithRequiredType<GeneratePersonalizedSlipActionStep>;
}) {
  return (
    <>
      {"Generate "}
      <Text fontWeight="semibold">Personalized Insert</Text>
      {" using "}
      <Text fontWeight="semibold">
        {step.personalizedSlipTemplateName || ""}
      </Text>
    </>
  );
}

function SetThirdPartyBillingAccountDetails({
  step,
}: {
  step: PartialWithRequiredType<SetThirdPartyBillingAccountActionStep>;
}) {
  return (
    <>
      {"Set "}
      <Text fontWeight="semibold">Third party billing account</Text>
      {" to "}
      <Text fontWeight="semibold">{step.thirdPartyBillingAccountName}</Text>
    </>
  );
}

function SetOverrideUseOriginalDestinationAddressDetails({
  step,
}: {
  step: PartialWithRequiredType<SetOverrideUseOriginalDestinationAddressActionStep>;
}) {
  return (
    <Flex dir="row" gap="xs">
      {step.overrideUseOriginalDestinationAddress ? (
        <>
          <Text fontWeight="semibold">
            Use original, unverified destination address
          </Text>
          <InfoTooltip
            boxSize={16}
            tooltip={{
              title:
                "Use the original destination address instead of the verified address, even when the auto clean addresses setting is enabled",
            }}
          />
        </>
      ) : (
        <Text fontWeight="semibold">Do nothing</Text>
      )}
    </Flex>
  );
}

function SetDeliveryMethodDetails({
  step,
}: {
  step: PartialWithRequiredType<SetDeliveryMethodActionStep>;
}) {
  return (
    <>
      {"Set "}
      <Text fontWeight="semibold">delivery method</Text>
      {" to "}
      <Text fontWeight="semibold">
        {step.deliveryMethod
          ? RedoShopifyDeliveryMethodTypeToString[step.deliveryMethod]
          : "Not set"}
      </Text>
    </>
  );
}

function AddTagDetails({
  step,
}: {
  step: PartialWithRequiredType<AddTagActionStep>;
}) {
  return (
    <>
      {"Add "}
      <Text fontWeight="semibold">tags</Text>{" "}
      <Flex flex={1} pl="sm">
        <TagList tags={(step.tags || []) as Tag[]} />
      </Flex>
    </>
  );
}

function AddOrderTagsDetails({
  step,
}: {
  step: PartialWithRequiredType<AddOrderTagsActionStep>;
}) {
  const tags: Tag[] = useMemo(
    () =>
      (step.tags ?? []).map((name) => ({
        name,
        pillTheme: PillTheme.NORMAL,
        source: FulfillmentGroupTagSource.MERCHANT,
        kind: TagKind.FULFILLMENT_GROUP,
      })),
    [step.tags],
  );
  return (
    <>
      {"Add "}
      <Text fontWeight="semibold">order tags</Text>{" "}
      <Flex flex={1} pl="sm">
        <TagList tags={tags} />
      </Flex>
    </>
  );
}

function SetShippingOptionsDetails({
  step,
}: {
  step: PartialWithRequiredType<SetShippingOptionsActionStep>;
}) {
  const options = [];

  if (step.shippingOptions?.signatureRequired) {
    options.push("Signature required");
  }

  if (step.shippingOptions?.adultSignatureRequired) {
    options.push("Adult signature required");
  }

  if (step.shippingOptions?.includeInsurance) {
    options.push("Add additional insurance");
  }

  if (step.shippingOptions?.hazmat === Hazmat.CLASS_9_UNMARKED_LITHIUM) {
    options.push("Shipment contains lithium batteries");
  }

  if (step.shippingOptions?.hazmat === Hazmat.CLASS_9_DRY_ICE) {
    options.push("Shipment contains dry ice");
  }

  return (
    <>
      {"Set "}
      <Text fontWeight="semibold">Shipping Options</Text>
      {" to "}
      <br />
      <ul>
        {options.length ? (
          options.map((option, index) => <li key={index}> - {option}</li>)
        ) : (
          <li>None</li>
        )}
      </ul>
    </>
  );
}

function PlaceHoldDetails({
  step,
}: {
  step: PartialWithRequiredType<PlaceHoldActionStep>;
}) {
  if (step.holdOptions?.holdType === AutoHoldType.Indefinite) {
    return (
      <>
        Place hold
        <Text fontWeight="semibold"> indefinitely</Text>
      </>
    );
  }

  const duration =
    step.holdOptions?.holdType === AutoHoldType.Duration &&
    step.holdOptions.duration
      ? step.holdOptions?.duration
      : 0;

  const timeUnit =
    step.holdOptions?.holdType === AutoHoldType.Duration &&
    step.holdOptions.timeUnit
      ? step.holdOptions?.timeUnit
      : TimeUnit.DAY;

  return (
    <>
      Place hold for
      <Text fontWeight="semibold"> {duration} </Text>
      {getTimeUnitName(timeUnit, duration)}
    </>
  );
}

function PlaceHoldUntilDetails({
  step,
}: {
  step: PartialWithRequiredType<PlaceHoldUntilActionStep>;
}) {
  const holdDate = step.holdOptions?.date;

  const formatDate = (date: Date) => {
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <>
      Place hold until
      <Text fontWeight="semibold">
        {" "}
        {holdDate instanceof Date ? formatDate(holdDate) : "N/A"}{" "}
      </Text>
    </>
  );
}

function SetWeightDetails({
  step,
}: {
  step: PartialWithRequiredType<SetWeightActionStep>;
}) {
  const team = useRequiredContext(TeamContext);

  if (!step.weight?.unit || !step.weight?.value) {
    return (
      <>
        Set weight to
        <Text fontWeight="semibold"> 0 </Text>
      </>
    );
  }

  const useMetric =
    team.settings?.outboundLabels?.weightFormat === WeightFormat.METRIC;

  let weightText: string;

  if (useMetric) {
    const [kg, g] = splitWeight(step.weight as Weight, [
      WeightUnit.KILOGRAM,
      WeightUnit.GRAM,
    ]);

    const parts = [];
    if (kg) {
      parts.push(`${kg} kg`);
    }

    if (g) {
      parts.push(`${g} g`);
    }

    weightText = parts.join(", ");
  } else {
    const [lbs, oz] = splitWeight(step.weight as Weight, [
      WeightUnit.POUND,
      WeightUnit.OUNCE,
    ]);

    const parts = [];
    if (lbs === 1) {
      parts.push(`${lbs} lb`);
    } else if (lbs > 1) {
      parts.push(`${lbs} lbs`);
    }

    if (oz) {
      parts.push(`${oz} oz`);
    }

    weightText = parts.join(", ");
  }

  return (
    <>
      Set weight to
      <Text fontWeight="semibold"> {weightText} </Text>
    </>
  );
}

function RoundWeightDetails({
  step,
}: {
  step: PartialWithRequiredType<RoundWeightActionStep>;
}) {
  const team = useRequiredContext(TeamContext);

  const roundingText =
    step.roundingType === WeightRoundingType.ROUND_UP
      ? "up"
      : step.roundingType === WeightRoundingType.ROUND_DOWN
        ? "down"
        : "unset";

  if (!step.minimumWeight?.unit || !step.minimumWeight?.value) {
    return (
      <>
        <Text fontWeight="semibold">Round weight {roundingText}</Text>
        {" when ≥ "}
        <Text fontWeight="semibold">1 lb</Text>
      </>
    );
  }

  const useMetric =
    team.settings?.outboundLabels?.weightFormat === WeightFormat.METRIC;

  let weightText: string;

  if (useMetric) {
    const [kg, g] = splitWeight(step.minimumWeight as Weight, [
      WeightUnit.KILOGRAM,
      WeightUnit.GRAM,
    ]);

    const parts = [];
    if (kg) {
      parts.push(`${kg} kg`);
    }

    if (g) {
      parts.push(`${g} g`);
    }

    weightText = parts.join(", ");
  } else {
    const [lbs, oz] = splitWeight(step.minimumWeight as Weight, [
      WeightUnit.POUND,
      WeightUnit.OUNCE,
    ]);

    const parts = [];
    if (lbs === 1) {
      parts.push(`${lbs} lb`);
    } else if (lbs > 1) {
      parts.push(`${lbs} lbs`);
    }

    if (oz) {
      parts.push(`${oz} oz`);
    }

    weightText = parts.join(", ");
  }

  return (
    <>
      <Text fontWeight="semibold">Round weight {roundingText}</Text>
      {" when ≥ "}
      <Text fontWeight="semibold">{weightText}</Text>
    </>
  );
}

function SetAssignedUserDetails({
  step,
}: {
  step: PartialWithRequiredType<SetAssignedUserActionStep>;
}) {
  return (
    <>
      {"Set "}
      <Text fontWeight="semibold">assigned user</Text>
    </>
  );
}

function SetDutiesPayerDetails({
  step,
}: {
  step: PartialWithRequiredType<SetDutiesPayerActionStep>;
}) {
  return (
    <>
      {"Set "}
      <Text fontWeight="semibold">duties payer</Text>
      {step.ddp != null && (
        <Text fontWeight="semibold">
          {" "}
          to {step.ddp ? "shipper paid" : "customer paid"}{" "}
        </Text>
      )}
    </>
  );
}

function SetTaxIdentifiersDetails({
  step,
}: {
  step: PartialWithRequiredType<SetTaxIdentifiersActionStep>;
}) {
  return (
    <>
      {"Set "}
      <Text fontWeight="semibold">tax identifiers</Text>
    </>
  );
}

function SetReturnAddressDetails({
  step,
}: {
  step: PartialWithRequiredType<SetReturnAddressActionStep>;
}) {
  return (
    <>
      {"Set "}
      <Text fontWeight="semibold">return address</Text>
      {" to "}
      <Text fontWeight="semibold">{step.address?.name}</Text>
    </>
  );
}

function SetOriginLocationDetails({
  step,
}: {
  step: PartialWithRequiredType<SetOriginLocationActionStep>;
}) {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const locationsLoad = useLoad(async () => {
    try {
      const { locations } = await rpcClient.getLocations(null);
      return locations;
    } catch (error) {
      console.error(error);
      return [];
    }
  }, []);

  const locationsMap = useMemo(() => {
    const map = new Map<string, RedoFulfillmentLocation>();
    if (locationsLoad.value) {
      for (const location of locationsLoad.value) {
        map.set(location.externalId, location);
      }
    }
    return map;
  }, [locationsLoad.value]);

  const locationName = step.locationId
    ? locationsMap.get(step.locationId)?.name
    : undefined;

  return (
    <>
      {"Set "}
      <Text fontWeight="semibold">origin location</Text>
      {step.locationId && (
        <>
          {" to "}
          <Text fontWeight="semibold">{locationName || step.locationId}</Text>
        </>
      )}
    </>
  );
}

function SetFulfillmentServiceDetails({
  step,
}: {
  step: PartialWithRequiredType<AssignFulfillmentServiceActionStep>;
}) {
  return (
    <>
      {"Set "}
      <Text fontWeight="semibold">fulfillment service</Text>
      {" to "}
      <Text fontWeight="semibold">{step.fulfillmentServiceName}</Text>
    </>
  );
}

function SetBuyReturnLabelDetails({
  step,
}: {
  step: PartialWithRequiredType<SetBuyReturnLabelActionStep>;
}) {
  return (
    <>
      {"Set "}
      <Text fontWeight="semibold">return label preference</Text>
      {" to "}
      <Text fontWeight="semibold">
        {step.buyReturnLabel ? "enabled" : "disabled"}
      </Text>
    </>
  );
}
function SetInternalNoteDetails({
  step,
}: {
  step: PartialWithRequiredType<SetInternalNoteActionStep>;
}) {
  return (
    <>
      {"Set "}
      <Text fontWeight="semibold">internal note</Text>
      {step.note && (
        <>
          {" to "}
          <Text fontWeight="semibold">
            {step.note.length > 50
              ? `${step.note.substring(0, 50)}...`
              : step.note}
          </Text>
        </>
      )}
    </>
  );
}

// search-tag:ADDING-AN-OMS-ACTION
export function OmsActionNode(data: {
  id: string;
  data: OmsActionNodeData;
  selected?: boolean | undefined;
}) {
  let details: React.ReactNode = null;
  switch (data.data.step.field) {
    case OmsSettableFields.SHIPPING_SERVICE:
      details = <SetShippingServiceDetails step={data.data.step} />;
      break;
    case OmsSettableFields.PARCEL:
      details = <SetParcelDetails step={data.data.step} />;
      break;
    case OmsSettableFields.SHIPPING_SERVICE_CHEAPEST:
      details = <SetShippingServiceGroupDetails step={data.data.step} />;
      break;
    case OmsSettableFields.SHIPPING_SERVICE_FASTEST:
      details = <SetShippingServiceGroupDetails step={data.data.step} />;
      break;
    case OmsSettableFields.SHIPPING_SERVICE_CHEAPEST_WITHIN_DELIVERY_DAYS:
      details = <SetShippingServiceGroupDetails step={data.data.step} />;
      break;
    case OmsSettableFields.PACKING_SLIP_TEMPLATE:
      details = <SetPackingSlipTemplateDetails step={data.data.step} />;
      break;
    case OmsSettableFields.TAG:
      details = <AddTagDetails step={data.data.step} />;
      break;
    case OmsSettableFields.SHIPPING_OPTIONS:
      details = <SetShippingOptionsDetails step={data.data.step} />;
      break;
    case OmsSettableFields.PLACE_HOLD:
      details = <PlaceHoldDetails step={data.data.step} />;
      break;
    case OmsSettableFields.PLACE_HOLD_UNTIL:
      details = <PlaceHoldUntilDetails step={data.data.step} />;
      break;
    case OmsSettableFields.SET_WEIGHT:
      details = <SetWeightDetails step={data.data.step} />;
      break;
    case OmsSettableFields.ROUND_WEIGHT:
      details = <RoundWeightDetails step={data.data.step} />;
      break;
    case OmsSettableFields.ASSIGNED_USER:
      details = <SetAssignedUserDetails step={data.data.step} />;
      break;
    case OmsSettableFields.DUTIES_PAYER:
      details = <SetDutiesPayerDetails step={data.data.step} />;
      break;
    case OmsSettableFields.TAX_IDENTIFIERS:
      details = <SetTaxIdentifiersDetails step={data.data.step} />;
      break;
    case OmsSettableFields.RETURN_ADDRESS:
      details = <SetReturnAddressDetails step={data.data.step} />;
      break;
    case OmsSettableFields.FULFILLMENT_SERVICE:
      details = <SetFulfillmentServiceDetails step={data.data.step} />;
      break;
    case OmsSettableFields.ORIGIN_LOCATION:
      details = <SetOriginLocationDetails step={data.data.step} />;
      break;
    case OmsSettableFields.BUY_RETURN_LABEL:
      details = <SetBuyReturnLabelDetails step={data.data.step} />;
      break;
    case OmsSettableFields.THIRD_PARTY_BILLING_ACCOUNT:
      details = <SetThirdPartyBillingAccountDetails step={data.data.step} />;
      break;
    case OmsSettableFields.DELIVERY_METHOD:
      details = <SetDeliveryMethodDetails step={data.data.step} />;
      break;
    case OmsSettableFields.OVERRIDE_USE_ORIGINAL_DESTINATION_ADDRESS:
      details = (
        <SetOverrideUseOriginalDestinationAddressDetails
          step={data.data.step}
        />
      );
      break;
    case OmsSettableFields.PERSONALIZED_SLIP:
      details = <PersonalizedSlipDetails step={data.data.step} />;
      break;
    case OmsSettableFields.INTERNAL_NOTE:
      details = <SetInternalNoteDetails step={data.data.step} />;
      break;
    case OmsSettableFields.SPLIT_INTO_PARCELS:
      details = <Text>Split items into own parcels</Text>;
      break;
    case OmsSettableFields.ORDER_TAG:
      details = <AddOrderTagsDetails step={data.data.step} />;
      break;
    case undefined:
      details = <Text textColor="disabled">Do nothing</Text>;
      break;
    default:
      assertNever(data.data.step);
  }
  return (
    <>
      <Handle position={Position.Top} type="target" />
      <BaseNode
        icon={<RedoFeaturedIcon color="success" Icon={PlayCircle} size="xs" />}
        onDelete={() => data.data.deleteElements(data.id)}
        selected={data.selected}
        step={data.data.step}
        title="Action"
      >
        <Flex
          className={NodeCSS.fullInput}
          fontSize="xs"
          gap="none"
          wrap="wrap"
        >
          {details}
        </Flex>
      </BaseNode>
      <Handle position={Position.Bottom} type="source" />
    </>
  );
}
